import { Component, Input, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  MonitoringFilter,
  MonitoringService,
  MultiuserMonitoringSorting,
} from 'src/providers/monitoring.service';
import { PresenceMonitoringEmployee } from 'src/shared/models/multiuser-monitoring-employee/multiuser-employee';
import { StorageProvider } from 'src/providers/storage.provider';
import { TranslatorProvider } from 'src/providers/translator.provider';
import { ActionSheetButton, ActionSheetController, ToastController } from '@ionic/angular';
import { PushManagerProvider } from 'src/providers/push/push-manager.provider';
import { NotificationType } from 'src/shared/models/push-notification/push-notification';
import { TabsProvider } from 'src/providers/tabs/tabs.provider';
import { MonitoringListComponent } from './monitoring-list/monitoring-list.page';
import { HapticsProvider, HapticType } from 'src/providers/haptics.provider';
import Swiper from 'swiper';

@Component({
  selector: 'rota-page-presence-monitoring',
  styleUrl: 'presence-monitoring.page.scss',
  templateUrl: 'presence-monitoring.page.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PresenceMonitoringPage {
  swiper?: Swiper;

  @Input() isModalView: boolean;

  @ViewChild('listForPage1') list1: MonitoringListComponent;
  @ViewChild('listForPage2') list2: MonitoringListComponent;
  @ViewChild('listForPage3') list3: MonitoringListComponent;
  @ViewChild('listForPage4') list4: MonitoringListComponent;

  headerTitles = signal<string[]>([]);
  headerTitle = signal<string>('');

  isPageLoaded = signal<boolean>(false);

  filter = signal<MonitoringFilter | undefined>(undefined);

  employeeListSub: Subscription;
  monitoringSettingsSub: Subscription;

  autoReloadMS = 5000;
  isAutoReload = false;
  refreshDataInterval: any;

  isLoading = signal<boolean>(false);

  employeeList = signal<PresenceMonitoringEmployee[]>([]);

  get isDesktopView() {
    return this.storage.isDesktopPlatform;
  }

  get prevSlideExist() {
    return this.swiper?.activeIndex! > 0;
  }

  get nextSlideExist() {
    return this.swiper?.activeIndex! < 4 - 1;
  }

  get isFilterOpen() {
    return false;
  }

  get isFilterActive(): boolean {
    const filter = this.filter();
    return (
      !!filter &&
      (filter.byDepartmentId !== -1 ||
        (filter.sortBy !== MultiuserMonitoringSorting.None &&
          filter.sortBy !== MultiuserMonitoringSorting.MonitorMode))
    );
  }

  constructor(
    private monitoringService: MonitoringService,
    private storage: StorageProvider,
    private translator: TranslatorProvider,
    private actionSheetController: ActionSheetController,
    private pushManagerProvider: PushManagerProvider,
    private toastController: ToastController,
    private tabsProvider: TabsProvider,
    private hapticsProvider: HapticsProvider
  ) {}

  async ionViewWillEnter(): Promise<void> {
    this.headerTitles.set([
      this.translator.dictionary.Monitoring_All_Employees_title,
      this.translator.dictionary.Monitoring_Present_title,
      this.translator.dictionary.Monitoring_Break_title,
      this.translator.dictionary.Monitoring_Not_present_or_sheduled_title,
    ]);

    this.headerTitle.set(this.headerTitles()[0]);

    this.employeeListSub = this.monitoringService.employeeList$.subscribe((data) => {
      if (!data) return;

      this.isPageLoaded.set(data && data.employeeList.length > 0);

      this.filter.set(data.filter);

      const isUploadedNewEmployees = this.isNewEmployees(
        this.employeeList(),
        data.employeeList,
        data.filter.sortBy
      );
      if (isUploadedNewEmployees) this.showNewEmployeesToast();

      this.employeeList.set(data.employeeList);
    });

    this.isLoading.set(true);
    await this.monitoringService.updateMonitoringEmployees();
    this.readPresenceMonitoring();
    this.isLoading.set(false);

    this.subscribeteToMonitoringMode();
  }

  swiperReady(e: any) {
    this.swiper = e.detail[0];

    this.swiper?.keyboard.enable();
  }

  onChangeSlide() {
    this.slideChanged();
  }

  async readPresenceMonitoring() {
    if (this.pushManagerProvider.isRead(NotificationType.PresenceMonitoring)) return;

    const isRead = await this.monitoringService.read();
    if (isRead) {
      await this.pushManagerProvider.fetchNotifications();
    }
  }

  applyFilter(filter: MonitoringFilter) {
    this.filter.set(filter);

    this.list1.applyFilter(filter);
    this.list2.applyFilter(filter);
    this.list3.applyFilter(filter);
    this.list4.applyFilter(filter);
  }

  handleSearch(search: CustomEvent) {
    this.list1.searchTyping(search.detail.value);
    this.list2.searchTyping(search.detail.value);
    this.list3.searchTyping(search.detail.value);
    this.list4.searchTyping(search.detail.value);
  }

  async toggleFilter() {
    await this.showFilters();
  }

  async showFilters() {
    const sheetFilterBy = await this.actionSheetController.create({
      header: this.translator.dictionary.Sort_by,
      buttons: [
        {
          text: this.translator.dictionary.Employee,
          role: this.filter()?.sortBy === MultiuserMonitoringSorting.EmployeeName ? 'selected' : '',
          data: MultiuserMonitoringSorting.EmployeeName,
        },
        {
          text: this.translator.dictionary.Person_ID,
          role: this.filter()?.sortBy === MultiuserMonitoringSorting.PersonId ? 'selected' : '',
          data: MultiuserMonitoringSorting.PersonId,
        },
        {
          text: this.translator.dictionary.Department,
          role:
            this.filter()?.sortBy === MultiuserMonitoringSorting.Departments &&
            this.filter()?.byDepartmentId === -1
              ? 'selected'
              : '',
          data: MultiuserMonitoringSorting.Departments,
        },
        {
          text: 'Status',
          role: this.filter()?.sortBy === MultiuserMonitoringSorting.Status ? 'selected' : '',
          data: MultiuserMonitoringSorting.Status,
        },
        {
          text:
            this.filter()?.sortBy === MultiuserMonitoringSorting.DepartmentId
              ? this.getSelectedDepartmentName()
              : this.translator.dictionary.Specific_Department,
          role:
            this.filter()?.sortBy === MultiuserMonitoringSorting.DepartmentId &&
            this.filter()?.byDepartmentId !== -1
              ? 'selected'
              : '',
          data: MultiuserMonitoringSorting.DepartmentId,
        },
        {
          text: this.translator.dictionary.None,
          role: this.filter()?.sortBy === MultiuserMonitoringSorting.None ? 'selected' : '',
          data: MultiuserMonitoringSorting.None,
        },

        { text: this.translator.dictionary.General_Cancel, role: 'cancel' },
      ],
      translucent: true,
    });

    sheetFilterBy.onWillDismiss().then(async (data) => {
      if (data.role === 'cancel' || data.role === 'backdrop') return;

      this.hapticsProvider.vibrate(HapticType.Click);

      if (data.data === MultiuserMonitoringSorting.DepartmentId) {
        await this.showSpecificDepartments();
      } else if (data.data === this.filter()?.sortBy) {
        this.applyFilter({
          byDepartmentId: -1,
          sortBy: MultiuserMonitoringSorting.None,
        });
      } else if (data.role !== 'cancel' && data.role !== 'backdrop') {
        this.applyFilter({
          byDepartmentId: -1,
          sortBy: data.data,
        });
      }
    });

    await sheetFilterBy.present();
  }

  async showSpecificDepartments() {
    const departments = this.monitoringService.getMonitoringGroupNames().sort((s1, s2) => {
      if (`${s1.name}` > `${s2.name}$`) return 1;
      if (`${s1.name}` < `${s2.name}$`) return -1;
      return 0;
    });

    const buttons: ActionSheetButton[] = departments.map(
      (d) =>
        ({
          text: d.name,
          data: d.id,
        }) as ActionSheetButton
    );
    buttons.push({ text: this.translator.dictionary.General_Cancel, role: 'cancel' });

    const sheetSpecificDepartments = await this.actionSheetController.create({
      header: this.translator.dictionary.Sort_by,
      buttons: buttons,
      translucent: true,
    });

    sheetSpecificDepartments.onWillDismiss().then(async (data) => {
      if (data.role === 'cancel' || data.role === 'backdrop') return;

      this.hapticsProvider.vibrate(HapticType.Click);

      this.applyFilter({
        byDepartmentId: data.data,
        sortBy: MultiuserMonitoringSorting.DepartmentId,
      });
    });

    await sheetSpecificDepartments.present();
  }

  getSelectedDepartmentName() {
    const departments = this.monitoringService.getMonitoringGroupNames().sort((s1, s2) => {
      if (`${s1.name}` > `${s2.name}$`) return 1;
      if (`${s1.name}` < `${s2.name}$`) return -1;
      return 0;
    });

    if (this.filter()?.sortBy === MultiuserMonitoringSorting.DepartmentId) {
      const selectedDepartment = departments.find((d) => d.id === this.filter()?.byDepartmentId);
      if (selectedDepartment) return selectedDepartment.name;
    }

    return this.translator.dictionary.Specific_Department;
  }

  get isMonitoringMode() {
    return this.filter()?.sortBy === MultiuserMonitoringSorting.MonitorMode;
  }

  async toggleMonitoringMode() {
    this.applyFilter({
      byDepartmentId: -1,
      sortBy: this.isMonitoringMode
        ? MultiuserMonitoringSorting.None
        : MultiuserMonitoringSorting.MonitorMode,
    });

    const toast = await this.toastController.create({
      header: this.isMonitoringMode ? 'Monitoring mode is active' : 'Monitoring mode is inactive',
      duration: 2000,
      translucent: true,
      position: 'top',
      icon: 'bulb-outline',
      swipeGesture: 'vertical',
      cssClass: 'custom-double-top-toast',
    });

    await toast.present();
  }

  slideChanged() {
    const index = this.swiper!.activeIndex;
    if (index >= this.headerTitles().length) return;
    this.headerTitle.set(this.headerTitles()[index]);
  }

  prevSlide() {
    this.swiper?.slidePrev();
  }

  nextSlide() {
    this.swiper?.slideNext();
  }

  private isNewEmployees(
    oldEmployees: PresenceMonitoringEmployee[],
    newEmployees: PresenceMonitoringEmployee[],
    sortBy: MultiuserMonitoringSorting
  ) {
    if (sortBy !== MultiuserMonitoringSorting.MonitorMode || !oldEmployees) return false;

    if (oldEmployees.length !== newEmployees.length) return true;

    for (let i = 0; i < newEmployees.length; i++) {
      const element = newEmployees[i];

      if (oldEmployees.findIndex((e) => e.personId === element.personId) === -1) {
        return true;
      }
    }

    return false;
  }

  private async showNewEmployeesToast() {
    const toast = await this.toastController.create({
      header: 'New employees have been loaded',
      duration: 5000,
      translucent: true,
      position: 'top',
      icon: 'bulb-outline',
      swipeGesture: 'vertical',
    });

    await toast.present();
  }

  private subscribeteToMonitoringMode() {
    const minReloadMonitoringTimeoutMS = 4000;

    this.monitoringSettingsSub = this.monitoringService.monitoringSettings$.subscribe(
      (settings) => {
        this.isAutoReload =
          settings != null && !(settings?.reloadTime === 0 || settings?.reloadTime === -1);

        clearInterval(this.refreshDataInterval);

        if (!settings || !this.isAutoReload) {
          return;
        }

        this.autoReloadMS = settings.reloadTime * 1000;

        this.refreshDataInterval = setInterval(
          async () => {
            if (
              !this.isAutoReload ||
              this.filter()?.sortBy !== MultiuserMonitoringSorting.MonitorMode
            )
              return;

            await this.monitoringService.updateMonitoringEmployees(this.filter()!);
          },
          this.autoReloadMS <= minReloadMonitoringTimeoutMS
            ? minReloadMonitoringTimeoutMS
            : this.autoReloadMS
        );
      }
    );
  }

  async goBack() {
    await this.tabsProvider.forwardBack();
  }

  ionViewWillLeave(): void {
    if (this.employeeListSub) this.employeeListSub.unsubscribe();
    if (this.monitoringSettingsSub) this.monitoringSettingsSub.unsubscribe();
    this.employeeList.set([]);
    clearInterval(this.refreshDataInterval);
  }
}
